import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, Sparkles, Clock } from "lucide-react";
import TutorPanel from "@/components/TutorPanel";

const TIMED_EXAM_SECONDS = 90 * 60;

export default function QuizPage() {
  const { moduleId } = useParams();
  const location = useLocation();
  const isExam = location.pathname.includes("practice-exam");
  const isMissed = location.pathname.includes("missed-drill");
  const isSampleTest = location.pathname.includes("sample-test");
  const timed = new URLSearchParams(location.search).get("timed") === "1";
  const nav = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tutorOpen, setTutorOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(timed ? TIMED_EXAM_SECONDS : null);
  const submittedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const url = isMissed
          ? "/quizzes/missed"
          : isExam
          ? "/quizzes/practice-exam"
          : isSampleTest
          ? "/quizzes/sample-test"
          : `/quizzes/module/${moduleId}`;
        const { data } = await api.get(url);
        setQuestions(data.questions);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Timer effect
  useEffect(() => {
    if (!timed || result) return;
    if (timeLeft === null) return;
    if (timeLeft <= 0) {
      if (!submittedRef.current) submit();
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [timeLeft, timed, result]);

  const q = questions[current];
  const total = questions.length;
  const answeredCount = Object.keys(answers).length;

  async function submit() {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const modeLabel = isMissed ? "missed_drill" : isExam ? "practice_exam" : isSampleTest ? "sample_test" : "module";
    const lessonLabel = isMissed ? "missed-drill" : isExam ? "practice-exam" : isSampleTest ? "3-sample-test" : `${moduleId}-quiz`;
    const { data } = await api.post("/quizzes/submit", {
      module_id: isExam || isMissed ? 0 : isSampleTest ? 3 : parseInt(moduleId),
      lesson_id: lessonLabel,
      mode: modeLabel === "missed_drill" ? "practice_exam" : modeLabel,
      answers,
    });
    setResult(data);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (loading) return <AppLayout><div className="p-12 text-neutral-500">Loading questions…</div></AppLayout>;
  if (!questions.length) return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="quiz-empty">
        <div className="font-serif text-3xl text-[var(--dw-navy)] mb-3">Nothing to drill yet</div>
        <div className="text-neutral-600 mb-6">
          {isMissed
            ? "You haven't missed any questions yet — take a module quiz first, and the ones you get wrong will land here."
            : "No questions found."}
        </div>
        <Link to="/dashboard" className="dw-btn-primary rounded-sm px-5 py-2.5 text-sm inline-flex items-center gap-2">Back to dashboard</Link>
      </div>
    </AppLayout>
  );

  if (result) return <ResultView result={result} isExam={isExam} isMissed={isMissed} isSampleTest={isSampleTest} moduleId={moduleId} onRetake={() => window.location.reload()} />;

  const timeLow = timed && timeLeft !== null && timeLeft < 300;
  const backTo = isMissed ? "/dashboard" : isExam ? "/practice-exam" : isSampleTest ? "/modules/3" : `/modules/${moduleId}`;

  return (
    <AppLayout>
      <div className="min-h-[calc(100vh-0px)]">
        {/* Top progress bar */}
        <div className="sticky top-0 z-30 bg-[var(--dw-parchment)]/95 backdrop-blur-lg border-b border-[var(--dw-navy)]/8">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center gap-4">
            <Link to={backTo} className="text-neutral-500 hover:text-[var(--dw-navy)]" data-testid="quiz-back">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <div className="flex-1 h-1 bg-[var(--dw-navy)]/10 rounded-sm overflow-hidden">
              <div className="dw-progress-bar h-full" style={{ width: `${((current + 1) / total) * 100}%` }} />
            </div>
            <div className="font-mono-plex text-xs text-neutral-600 whitespace-nowrap" data-testid="question-counter">
              {current + 1} / {total}
            </div>
            {timed && timeLeft !== null && (
              <div
                data-testid="exam-timer"
                className={`font-mono-plex text-xs px-2 py-1 rounded-sm border whitespace-nowrap flex items-center gap-1 ${
                  timeLow ? "border-red-400 bg-red-50 text-red-700 animate-pulse" : "border-[var(--dw-navy)]/20 text-[var(--dw-navy)]"
                }`}
              >
                <Clock className="w-3 h-3" strokeWidth={1.5} /> {formatTime(timeLeft)}
              </div>
            )}
            <button
              onClick={() => setTutorOpen(true)}
              data-testid="quiz-tutor-btn"
              className="hidden md:flex items-center gap-1 text-xs text-neutral-600 hover:text-[var(--dw-navy)] px-3 py-1.5 border border-[var(--dw-navy)]/15 rounded-sm"
            >
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Ask tutor
            </button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-12" data-testid="quiz-page">
          <div className="overline mb-3">
            {isMissed ? "Missed-Question Drill" : isExam ? (timed ? "Timed Practice Exam" : "Practice Exam") : isSampleTest ? "Sample Test · General Knowledge & Math" : `Module ${moduleId} · Knowledge Check`} · Question {q.qn}
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-[var(--dw-navy)] leading-snug mb-8" data-testid="question-text">
            {q.q}
          </h2>

          <div className="space-y-2.5">
            {Object.entries(q.choices).map(([letter, text]) => {
              const selected = answers[q.qn] === letter;
              return (
                <button
                  key={letter}
                  onClick={() => setAnswers({ ...answers, [q.qn]: letter })}
                  data-testid={`choice-${letter}`}
                  className={`w-full text-left flex items-start gap-4 p-4 border rounded-sm transition-all ${
                    selected
                      ? "border-[var(--dw-navy)] bg-[var(--dw-navy)]/[0.03]"
                      : "border-[var(--dw-navy)]/12 hover:border-[var(--dw-navy)]/40 bg-white"
                  }`}
                >
                  <div className={`w-8 h-8 shrink-0 flex items-center justify-center border rounded-sm font-mono-plex text-sm ${
                    selected ? "border-[var(--dw-navy)] bg-[var(--dw-navy)] text-[var(--dw-parchment)]" : "border-[var(--dw-navy)]/25 text-[var(--dw-navy)]"
                  }`}>{letter}</div>
                  <div className="text-sm text-neutral-800 leading-relaxed pt-1">{text}</div>
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="mt-10 flex items-center justify-between">
            <button
              onClick={() => setCurrent(Math.max(0, current - 1))}
              disabled={current === 0}
              data-testid="prev-question"
              className="dw-btn-outline rounded-sm px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-40"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Previous
            </button>

            <div className="text-xs text-neutral-500 hidden sm:block">
              {answeredCount} of {total} answered
            </div>

            {current === total - 1 ? (
              <button
                onClick={submit}
                disabled={answeredCount === 0}
                data-testid="submit-quiz"
                className="dw-btn-gold rounded-sm px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-40"
              >
                Submit <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            ) : (
              <button
                onClick={() => setCurrent(Math.min(total - 1, current + 1))}
                data-testid="next-question"
                className="dw-btn-primary rounded-sm px-4 py-2 text-sm inline-flex items-center gap-2"
              >
                Next <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
          </div>

          {/* Grid nav */}
          <div className="mt-10 pt-6 border-t border-[var(--dw-navy)]/10">
            <div className="overline mb-3">Question navigator</div>
            <div className="grid grid-cols-10 sm:grid-cols-15 gap-1">
              {questions.map((qq, i) => (
                <button
                  key={qq.qn}
                  onClick={() => setCurrent(i)}
                  data-testid={`nav-question-${i + 1}`}
                  className={`aspect-square text-[10px] font-mono-plex border rounded-sm transition-colors ${
                    i === current
                      ? "border-[var(--dw-navy)] bg-[var(--dw-navy)] text-[var(--dw-parchment)]"
                      : answers[qq.qn]
                      ? "border-[var(--dw-gold)] bg-[var(--dw-gold)]/10 text-[var(--dw-navy)]"
                      : "border-[var(--dw-navy)]/15 text-neutral-500 hover:border-[var(--dw-navy)]/40"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {tutorOpen && (
        <TutorPanel
          initialContext={`Current question: ${q.q}\nOptions:\n${Object.entries(q.choices).map(([l, t]) => `${l}. ${t}`).join("\n")}`}
          onClose={() => setTutorOpen(false)}
        />
      )}
    </AppLayout>
  );
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function ResultView({ result, isExam, isMissed, isSampleTest, moduleId, onRetake }) {
  // Module quizzes require 85%; practice exam and missed drill require 75%.
  const passThreshold = isExam || isMissed ? 75 : 85;
  const pass = result.score_pct >= passThreshold;
  const [expanded, setExpanded] = useState(false);

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-6 py-12" data-testid="quiz-result-page">
        {/* Score hero */}
        <div className="dw-card p-8 md:p-10 relative overflow-hidden mb-8">
          <div className="dw-grid-lines absolute inset-0 opacity-30" />
          <div className="relative">
            <div className="overline mb-2">Assessment complete</div>
            <div className="flex flex-wrap items-end gap-6 mb-6">
              <div className={`font-serif text-7xl leading-none ${pass ? "text-emerald-700" : "text-red-700"}`} data-testid="result-score">
                {result.score_pct}%
              </div>
              <div className="pb-2">
                <div className={`text-lg font-medium ${pass ? "text-emerald-700" : "text-red-700"}`}>
                  {pass ? "Passing score" : "Below passing threshold"}
                </div>
                <div className="text-sm text-neutral-600">
                  {result.correct} of {result.total} correct · Passing = {passThreshold}%
                </div>
              </div>
            </div>

            {/* By module breakdown */}
            {(isExam || isMissed) && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-6 border-t border-[var(--dw-navy)]/10">
                {[1, 2, 3, 4, 5].map((m) => {
                  const s = result.by_module?.[m];
                  if (!s) return null;
                  const p = Math.round((s.correct / s.total) * 100);
                  return (
                    <div key={m} className="text-center">
                      <div className="overline mb-1">Module {m}</div>
                      <div className={`font-serif text-2xl ${p >= 75 ? "text-emerald-700" : "text-red-700"}`}>{p}%</div>
                      <div className="text-xs text-neutral-500">{s.correct}/{s.total}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          <button onClick={onRetake} data-testid="retake-btn" className="dw-btn-primary rounded-sm px-5 py-2.5 text-sm">Retake</button>
          <Link to={isMissed ? "/dashboard" : isExam ? "/dashboard" : isSampleTest ? "/modules/3" : `/modules/${moduleId}`} data-testid="return-btn" className="dw-btn-outline rounded-sm px-5 py-2.5 text-sm">
            {isExam || isMissed ? "Return to dashboard" : isSampleTest ? "Back to Module 3" : `Back to Module ${moduleId}`}
          </Link>
          {result.results?.some((r) => !r.is_correct) && !isMissed && (
            <Link to="/missed-drill" data-testid="drill-missed" className="dw-btn-outline rounded-sm px-5 py-2.5 text-sm border-[var(--dw-gold)] text-[var(--dw-navy)]">
              Drill missed questions
            </Link>
          )}
          <button onClick={() => setExpanded(!expanded)} data-testid="review-toggle" className="dw-btn-outline rounded-sm px-5 py-2.5 text-sm">
            {expanded ? "Hide" : "Review"} answers
          </button>
        </div>

        {expanded && (
          <div className="space-y-3">
            {result.results.map((r, i) => (
              <div key={i} data-testid={`review-${i}`} className={`dw-card p-5 border-l-4 ${r.is_correct ? "border-l-emerald-600" : "border-l-red-600"}`}>
                <div className="flex items-start gap-3 mb-2">
                  {r.is_correct ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" strokeWidth={1.5} />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" strokeWidth={1.5} />
                  )}
                  <div className="font-mono-plex text-xs text-neutral-500">{r.qn} · Module {r.module}</div>
                </div>
                <div className="font-serif text-lg text-[var(--dw-navy)] mb-3">{r.question}</div>
                <div className="space-y-1.5 text-sm">
                  {Object.entries(r.choices).map(([l, t]) => {
                    const isCorrect = l === r.correct;
                    const isChosen = l === r.chosen;
                    return (
                      <div
                        key={l}
                        className={`px-3 py-2 border rounded-sm flex items-start gap-2 ${
                          isCorrect ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                          : isChosen ? "border-red-300 bg-red-50 text-red-900"
                          : "border-transparent text-neutral-600"
                        }`}
                      >
                        <span className="font-mono-plex text-xs shrink-0">{l}.</span>
                        <span>{t}</span>
                        {isCorrect && <CheckCircle2 className="w-4 h-4 ml-auto shrink-0" strokeWidth={1.5} />}
                        {!isCorrect && isChosen && <XCircle className="w-4 h-4 ml-auto shrink-0" strokeWidth={1.5} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
